import { useEffect, useMemo, useState } from "react";
import localization from "../../../config/localization";
import type { ActiveVerticalDetail } from "../../../features/home/models";
import homeService from "../../../features/home/services/home.service";
import { GameVertical } from "../../../models/constants/gameVertical";
import { localStorageService } from "../../../services";
import { Button, DraggableCarousel } from "../../ui";
import { sidebarMenu } from "./sidebarMenuConst";

const SPORTS_VERTICAL = GameVertical.Sports.toLowerCase();
const SAP_VERTICAL = GameVertical.SAP.toLowerCase();

const filterActiveVerticalsForNav = (
  details: ActiveVerticalDetail[],
  userVerticalsCsv: string | null | undefined,
) => {
  const userVerticalsList = (userVerticalsCsv ?? "")
    .toLowerCase()
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  return details.filter((vertical) => {
    const nameLower = vertical.name.toLowerCase();
    const isSportsIndia =
      nameLower === GameVertical.SportsIndia.toLowerCase() ||
      nameLower === "sports india";

    if (nameLower === SPORTS_VERTICAL) {
      return !userVerticalsList.includes(SPORTS_VERTICAL);
    }
    if (isSportsIndia) {
      return !userVerticalsList.includes(SAP_VERTICAL);
    }
    return !userVerticalsList.some((x) => x === nameLower);
  });
};

interface SidebarTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  className?: string;
}

export function SidebarTabs({
  activeTab,
  onTabChange,
  className,
}: SidebarTabsProps) {
  const isAuthenticated = localStorageService.getItem("token") !== null;
  const [activeVerticals, setActiveVerticals] = useState<ActiveVerticalDetail[]>(
    [],
  );
  const [showSportsIndia, setShowSportsIndia] = useState(true);

  useEffect(() => {
    const loadSidebarTabsVerticals = async () => {
      try {
        const response = await homeService.fetchActiveVerticals();
        if (!response.isSuccess || !response.data?.details) {
          setActiveVerticals([]);
          setShowSportsIndia(false);
          return;
        }

        let details = response.data.details;
        let sportsIndiaVisible = true;

        if (isAuthenticated) {
          const userVerticals = await homeService.fetchUserVerticals();
          if (userVerticals.isSuccess && userVerticals.data) {
            const userVerticalsList = (userVerticals.data.verticalsCsv ?? "")
              .toLowerCase()
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0);

            sportsIndiaVisible = !userVerticalsList.includes(SAP_VERTICAL);
            details = filterActiveVerticalsForNav(
              details,
              userVerticals.data.verticalsCsv,
            );
          }
        }

        setActiveVerticals(details);
        setShowSportsIndia(sportsIndiaVisible);
      } catch {
        setActiveVerticals([]);
        setShowSportsIndia(false);
      }
    };

    loadSidebarTabsVerticals();
  }, [isAuthenticated]);

  const showGamesTab = useMemo(() => {
    return activeVerticals.length > 0 || showSportsIndia;
  }, [activeVerticals, showSportsIndia]);

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        id: sidebarMenu.Account,
        label: localization.account,
        icon: "/icons/ic-sidebar-menu-account.svg",
        authRequired: false,
      },
      {
        id: sidebarMenu.Profile,
        label: localization.profile,
        icon: "/icons/ic-sidebar-menu-profile.svg",
        authRequired: true,
      },
      ...(showGamesTab
        ? [
            {
              id: sidebarMenu.Games,
              label: localization.games,
              icon: "/icons/ic-sidebar-menu-games.svg",
              authRequired: false,
            },
          ]
        : []),
      {
        id: sidebarMenu.Promotion,
        label: localization.promotion,
        icon: "/icons/ic-sidebar-menu-promotion.svg",
        authRequired: false,
      },
    ];

    return baseTabs;
  }, [showGamesTab]);

  const filteredTabs = tabs.filter(
    (tab) => !tab.authRequired || isAuthenticated,
  );

  return (
    <div className={`sticky top-0 z-50 ${className}`}>
      <div className="flex items-center justify-center px-3 sm:px-7 pt-4 pb-2 bg-[#121212]">
        <DraggableCarousel
          gap="gap-4 sm:gap-12"
          className="w-full flex justify-center"
        >
          {filteredTabs.map((tab) => {
            const isActive = activeTab === tab.id;

            return (
              <div key={tab.id} className="shrink-0 flex rounded-lg">
                <Button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className="flex flex-col items-center gap-1.5 min-w-20 sm:min-w-[64px]"
                >
                  <img
                    src={tab.icon}
                    alt={tab.label}
                    className="w-8 h-8 object-contain"
                  />

                  <span
                    className={` font-medium ${
                      isActive ? "text-white" : "text-gray-400"
                    }`}
                  >
                    {tab.label}
                  </span>

                  <span
                    className={`h-0.5 w-full rounded-full transition-all duration-300 ${
                      isActive ? "bg-red-500" : "bg-transparent"
                    }`}
                  />
                </Button>
              </div>
            );
          })}
        </DraggableCarousel>
      </div>
    </div>
  );
}
